import React, { useState, useEffect, useLayoutEffect } from "react";
import { Search, User, Copy, RotateCcw, Plus, Building2, Calendar, MapPin, Mail, Phone, Shield, X, Pen } from "lucide-react";
import "./EmployeeManagement.css";
import { AddEmployee } from "./addEmployee/AddEmployee";
import { deleteEmployeeUrl, editEmployeeUrl, fetchAllEmployees, sendLoginDetailsUrl } from "../../../api/endpoints";
import { useDispatch, useSelector } from "react-redux";
import { naira } from "../../stores/stockManagement/AddStockPage";
import EmployeeManagementSkeleton from "../../../components/uxCollections/skeletonLoader/employee/EmployeeSkeleton";
import { ButtonLoader } from "../../../components/uxCollections/Loaders";
import ActivityLog from "./actitvityLog/StaffActivity";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import { setUser } from "../../../slice/webStoreUserSlice";
import EditEmployeeOverlay from "./editEmploye/EditEmployeeOverlay";
import Loader from "../../../components/uxCollections/storelensSpiner/StorelenseSpinner";

export const EmployeeManagement = () => {
  const [employees, setEmployees] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState(employees);
  const [copiedField, setCopiedField] = useState("");
  const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
  const [isAddEmplyeeOpen, setIsAddEmplyeeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmployeeActivity, setShowEmployeeActivity] = useState(false);
  const [editEmployeeModalOpen, setEditEmployeeModalOpen] = useState(false);

  const { user } = useSelector((state) => state.webStoreUser);
  const currency = user.userCountryCurrencySymbol || naira; // Default to £ if not set

  useEffect(() => {
    if (!employees || employees.length === 0) {
      return;
    }
    const filtered = employees.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.staffID.includes(searchTerm) ||
        employee.position.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredEmployees(filtered);
    setSelectedEmployee(filtered[0] || null); // Set first employee as selected if available
  }, [searchTerm, employees]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const dispatch = useDispatch();

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      let response = await fetch(fetchAllEmployees, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (response.status === 404) {
        setEmployees([]); // Fallback to mock data if no employees found
        return;
      }

      if (response.ok) {
        const { employees } = await response.json();
        console.log("Fetched employees:", employees);

        setEmployees(employees); // Use mock data if API returns empty
        setSelectedEmployee(employees[0]); // Set first employee as selected
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      // Handle error (e.g., show a notification)
      uiAlert("Failed to fetch employees. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const generateNewPassword = () => {
    if (!selectedEmployee) return;

    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789#$@!";
    let newPassword = "";
    for (let i = 0; i < 12; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    const updatedEmployee = { ...selectedEmployee, password: newPassword };
    setSelectedEmployee(updatedEmployee);
  };

  const getAccessBadgeClass = (level) => {
    switch (level) {
      case "admin":
        return "emp-access-badge emp-access-admin";
      case "manager":
        return "emp-access-badge emp-access-manager";
      case "staff":
        return "emp-access-badge emp-access-staff";
      default:
        return "emp-access-badge";
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const sendCredentialsToEmail = async () => {
    setSendingEmail(true);
    try {
      let response = await fetch(sendLoginDetailsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ employee: selectedEmployee }),
      });
      if (response.ok) {
        uiAlert("Login credentials sent to employee's email successfully.", "success");
      }
    } catch (error) {
      console.error("Error sending credentials:", error);
      uiAlert("Failed to send credentials. Please try again later.", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteEmployee = async () => {
    console.log("Attempting to delete employee:", selectedEmployee);

    if (!selectedEmployee) return;

    if (!window.confirm(`Are you sure you want to revoke access and delete ${selectedEmployee.employeeName}? This action cannot be undone.`)) {
      return;
    }
    try {
      setLoading(true);
      let response = await fetch(deleteEmployeeUrl + selectedEmployee._id, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to delete employee`);
      }

      const { webstoreUser, deletedEmployeeID } = await response.json();

      dispatch(setUser(webstoreUser)); // Update user in Redux store
      // Remove employee from state
      setEmployees((prev) => prev.filter((emp) => emp._id !== deletedEmployeeID));
      setSelectedEmployee(null); // Clear selected employee

      uiAlert("Employee access revoked and deleted successfully.", "success");
    } catch (error) {
      console.log("Error deleting employee:", error);
      uiAlert("Failed to delete employee. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = async (updatedEmployee) => {
    setLoading(true);
    try {
      const payload = {
        employeeName: updatedEmployee.name,
        position: updatedEmployee.role,
        salary: updatedEmployee.salary,
        employeeImage: updatedEmployee.imageFile,
        employeePhoneNumber: updatedEmployee.phone,
        employeePassword: updatedEmployee.password,
        storeAssigned: updatedEmployee.storeAssigned,
        permissions: updatedEmployee.permissions,
        allowedActions: updatedEmployee.actions,
      };
      console.log("Payload for update:", payload);

      let response = await fetch(editEmployeeUrl + selectedEmployee._id, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.message || "Failed to update employee");
      }
      const { employee, message } = responseData;
      console.log("Employee updated successfully:", employee);
      // Update employee in state
      setEmployees((prev) => prev.map((emp) => (emp._id === employee._id ? employee : emp)));
      setSelectedEmployee(employee); // Update selected employee with new data
      uiAlert(`${message}, If permissions were changed, it might take up to 24-48hrs to reflect.` || "Employee updated successfully.", "success");
    } catch (error) {
      console.error("Error updating employee:", error);
      uiAlert(error.message || " Something went wrong, employee not updated. Please try again ", "error");
    } finally {
      setLoading(false);
    }
  };

  const whiteListUsers = ["nonsokoli@gmail.com", "blondchocolate29@gmail.com", "ogalivi500trd@gmail.com"];

  return (
    <>
      <div className='emp-container'>
        <div className='emp-max-width'>
          {/* Header */}
          <div className='emp-header'>
            <h1 className='emp-title'>Employee Management</h1>
            <p className='emp-subtitle'>Manage your team members and their access credentials</p>
          </div>

          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 190px)" }}>
              <Loader size={70} />
            </div>
          ) : (
            <>
              {!loading && employees && employees.length > 0 ? (
                <div className='emp-grid'>
                  {/* Employee List */}
                  <div className='emp-sidebar'>
                    {/* Search */}
                    <div className='emp-search-container'>
                      <Search className='emp-search-icon' />
                      <input
                        type='text'
                        placeholder='Search employee'
                        className='emp-search-input'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Filter Info */}
                    <div className='emp-filter-info'>
                      <span>All</span>
                      <span>count: {filteredEmployees?.length}</span>
                    </div>

                    {/* Employee Cards */}
                    <div className='emp-list'>
                      {filteredEmployees?.map((employee) => (
                        <div
                          key={employee.staffID}
                          className={`emp-card ${selectedEmployee?.staffID === employee.staffID ? "emp-card-active" : ""}`}
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setIsMobileDetailsOpen(true);
                          }}
                        >
                          <div className='emp-card-content'>
                            {employee.employeeImage ? (
                              <div className='emp-details-avatar'>
                                <img
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    border: "2px solid #ccc",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  }}
                                  src={employee.employeeImage}
                                  alt='Employee Avatar'
                                  className='emp-avatar-img'
                                />
                              </div>
                            ) : (
                              <div className='emp-details-avatar'>{getInitials(employee.employeeName)}</div>
                            )}
                            <div className='emp-card-info'>
                              <p className='emp-card-name'>{employee.employeeName}</p>
                              <p className='emp-card-position'>{employee.position}</p>
                              <p className='emp-card-store'>{employee.storeAssigned.storeName}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Employee Button */}
                    <button
                      onClick={() => {
                        if (whiteListUsers.includes(user.email) && user.employees.length <= 10) {
                          setIsAddEmplyeeOpen(true);
                          return;
                        }

                        if (user.userIsSubscribed && user.freeTrial) {
                          setIsAddEmplyeeOpen(true);
                          return;
                        }

                        if (user.userType.userType.toLowerCase().includes("basic") && user.employees.length >= 1) {
                          uiAlert("Your current plan allows only one employee. Please upgrade your plan to add more employees.", "error");
                          return;
                        }
                        if (user.userType.userType.toLowerCase().includes("super") && user.employees.length >= 2) {
                          uiAlert(" Your current plan allows only two employees. Please upgrade your plan to add more employees.", "error");
                          return;
                        }
                        setIsAddEmplyeeOpen(true);
                      }}
                      className='emp-add-btn'
                    >
                      <Plus className='emp-add-icon' />
                      <span>Add employee</span>
                    </button>
                  </div>

                  {/* Employee Details - Desktop */}
                  <div className='emp-details-container emp-details-desktop'>
                    {selectedEmployee ? (
                      <div className='emp-details-card'>
                        {/* Employee Header */}
                        <div className='emp-details-header'>
                          {selectedEmployee.employeeImage ? (
                            <div className='emp-details-avatar'>
                              <img
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                  border: "2px solid #ccc",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                }}
                                src={selectedEmployee.employeeImage}
                                alt='Employee Avatar'
                                className='emp-avatar-img'
                              />
                            </div>
                          ) : (
                            <div className='emp-details-avatar'>{getInitials(selectedEmployee?.employeeName)}</div>
                          )}
                          <div className='emp-details-info'>
                            <h2 className='emp-details-name'>{selectedEmployee?.employeeName}</h2>
                            <p className='emp-details-position'>{selectedEmployee?.position}</p>
                            <p className='emp-details-id'>ID: {selectedEmployee?.staffID}</p>
                            <div className='emp-badge-container'>
                              <span className={getAccessBadgeClass(selectedEmployee?.employeeAccessLevel)}>
                                <Shield className='emp-badge-icon' />
                                {selectedEmployee.employeeAccessLevel?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className='emp-details-grid'>
                          {/* Basic Information */}
                          <div className='emp-section'>
                            <h3 className='emp-section-title'>
                              <User className='emp-section-icon' />
                              Basic Information
                            </h3>

                            <div className='emp-section-content'>
                              <div className='emp-field'>
                                <label className='emp-field-label'>Store assigned</label>
                                <div className='emp-field-value'>
                                  <Building2 className='emp-field-icon' />
                                  <span>{selectedEmployee.storeAssigned?.storeName}</span>
                                </div>
                              </div>

                              <div className='emp-field'>
                                <label className='emp-field-label'>Salary</label>
                                <p className='emp-salary'>
                                  {currency} {selectedEmployee.salary?.toLocaleString() || "0"}
                                </p>
                              </div>

                              <div className='emp-field'>
                                <label className='emp-field-label'>Employment date</label>
                                <div className='emp-field-value'>
                                  <Calendar className='emp-field-icon' />
                                  <span>{selectedEmployee.dateOfEmployment.split("T")[0] || "N/A"}</span>
                                </div>
                              </div>

                              <div className='emp-field'>
                                <label className='emp-field-label'>Gender</label>
                                <p className='emp-field-text'>{selectedEmployee.gender}</p>
                              </div>

                              <div className='emp-field'>
                                <label className='emp-field-label'>Address</label>
                                <div className='emp-field-value'>
                                  <MapPin className='emp-field-icon' />
                                  <span>{selectedEmployee.employeeAddress}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Contact & Credentials */}
                          <div className='emp-section'>
                            <h3 className='emp-section-title'>
                              <Mail className='emp-section-icon' />
                              Contact & Credentials
                            </h3>

                            <div className='emp-section-content'>
                              <div className='emp-field'>
                                <label className='emp-field-label'>Email</label>
                                <div className='emp-field-value'>
                                  <Mail className='emp-field-icon' />
                                  <span>{selectedEmployee.employeeEmail}</span>
                                </div>
                              </div>

                              <div className='emp-field'>
                                <label className='emp-field-label'>Phone</label>
                                <div className='emp-field-value'>
                                  <Phone className='emp-field-icon' />
                                  <span>{selectedEmployee.employeePhoneNumber}</span>
                                </div>
                              </div>

                              {/* Username */}
                              <div className='emp-field'>
                                <label className='emp-field-label'>Username</label>
                                <div className='emp-credential-field'>
                                  <span className='emp-credential-text'>{selectedEmployee.employeUsername}</span>
                                  <button
                                    className='emp-credential-btn'
                                    onClick={() => copyToClipboard(selectedEmployee.employeUsername, "username")}
                                    title='Copy username'
                                  >
                                    <Copy className={`emp-credential-icon ${copiedField === "username" ? "emp-icon-success" : ""}`} />
                                  </button>
                                </div>
                              </div>

                              {/* Password */}
                              <div className='emp-field'>
                                <label className='emp-field-label'>Password</label>
                                <div className='emp-credential-field'>
                                  <span className='emp-credential-text'>{"*************"}</span>
                                  <div className='emp-credential-actions'>
                                    <button
                                      className='emp-credential-btn'
                                      onClick={() => copyToClipboard(selectedEmployee.password, "password")}
                                      title='Copy password'
                                    >
                                      <Copy className={`emp-credential-icon ${copiedField === "password" ? "emp-icon-success" : ""}`} />
                                    </button>
                                    {/* <button className='emp-credential-btn' onClick={generateNewPassword} title='Reset password'>
                                      <RotateCcw className='emp-credential-icon' />
                                    </button> */}
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className='emp-actions'>
                                {/* <button className='emp-btn-primary'>Edit Employee</button> */}
                                {/* <button className='emp-btn-secondary'>View Activity</button> */}
                                {sendingEmail ? (
                                  <button
                                    style={{
                                      opacity: 0.7,
                                      pointerEvents: "none",
                                    }}
                                    className='emp-btn-success'
                                  >
                                    <ButtonLoader size={20} color='#fff' />
                                  </button>
                                ) : (
                                  <button onClick={sendCredentialsToEmail} className='emp-btn-success'>
                                    Send login to email
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setShowEmployeeActivity(true);
                                  }}
                                  style={{
                                    backgroundColor: "rgba(199, 199, 199, 0.1)",
                                    color: "#000",
                                    border: "1px solid #ccc",
                                  }}
                                  className='emp-btn-success'
                                >
                                  View activity
                                </button>
                                <button
                                  onClick={handleDeleteEmployee}
                                  style={{
                                    backgroundColor: "#ff4d4d",
                                  }}
                                  className='emp-btn-success'
                                >
                                  Revoke Access and delete
                                </button>
                                <button
                                  onClick={() => {
                                    setEditEmployeeModalOpen(true);
                                  }}
                                  style={{
                                    backgroundColor: "rgba(199, 199, 199, 0.1)",
                                    color: "#000",
                                    border: "1px solid #ccc",
                                  }}
                                  className='emp-btn-success'
                                >
                                  Edit {<Pen size={12} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {copiedField && <div className='emp-toast'>{copiedField} copied to clipboard!</div>}
                      </div>
                    ) : (
                      <div className='emp-empty-state'>
                        <User className='emp-empty-icon' />
                        <h3 className='emp-empty-title'>No Employee Selected</h3>
                        <p className='emp-empty-text'>Select an employee from the list to view their details</p>
                      </div>
                    )}
                  </div>

                  {/* Mobile Details Overlay */}
                  {isMobileDetailsOpen && (
                    <>
                      <div className='emp-mobile-overlay' onClick={() => setIsMobileDetailsOpen(false)}></div>
                      <div className='emp-mobile-details'>
                        <div className='emp-mobile-details-header'>
                          <button className='emp-mobile-close-btn' onClick={() => setIsMobileDetailsOpen(false)}>
                            <X className='emp-mobile-close-icon' />
                          </button>
                        </div>

                        {selectedEmployee && (
                          <div className='emp-mobile-details-content'>
                            {/* Employee Header */}
                            <div className='emp-details-header'>
                              {selectedEmployee.employeeImage ? (
                                <div className='emp-details-avatar'>
                                  <img
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: "50%",
                                      border: "2px solid #ccc",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                    src={selectedEmployee.employeeImage}
                                    alt='Employee Avatar'
                                    className='emp-avatar-img'
                                  />
                                </div>
                              ) : (
                                <div className='emp-details-avatar'>{getInitials(selectedEmployee.employeeName)}</div>
                              )}
                              <div className='emp-details-info'>
                                <h2 className='emp-details-name'>{selectedEmployee.employeeName}</h2>
                                <p className='emp-details-position'>{selectedEmployee.position}</p>
                                <p className='emp-details-id'>ID: {selectedEmployee.staffID}</p>
                                <div className='emp-badge-container'>
                                  <span className={getAccessBadgeClass(selectedEmployee.employeeAccessLevel)}>
                                    <Shield className='emp-badge-icon' />
                                    {selectedEmployee.employeeAccessLevel.toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className='emp-details-grid'>
                              {/* Basic Information */}
                              <div className='emp-section'>
                                <h3 className='emp-section-title'>
                                  <User className='emp-section-icon' />
                                  Basic Information
                                </h3>

                                <div className='emp-section-content'>
                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Store assigned</label>
                                    <div className='emp-field-value'>
                                      <Building2 className='emp-field-icon' />
                                      <span>{selectedEmployee.storeAssigned.storeName}</span>
                                    </div>
                                  </div>

                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Salary</label>
                                    <p className='emp-salary'>
                                      {currency} {selectedEmployee.salary.toLocaleString()}
                                    </p>
                                  </div>

                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Employment date</label>
                                    <div className='emp-field-value'>
                                      <Calendar className='emp-field-icon' />
                                      <span>{selectedEmployee.dateOfEmployment.split("T")[0]}</span>
                                    </div>
                                  </div>

                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Gender</label>
                                    <p className='emp-field-text'>{selectedEmployee.gender}</p>
                                  </div>

                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Address</label>
                                    <div className='emp-field-value'>
                                      <MapPin className='emp-field-icon' />
                                      <span>{selectedEmployee.employeeAddress}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Contact & Credentials */}
                              <div className='emp-section'>
                                <h3 className='emp-section-title'>
                                  <Mail className='emp-section-icon' />
                                  Contact & Credentials
                                </h3>

                                <div className='emp-section-content'>
                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Email</label>
                                    <div className='emp-field-value'>
                                      <Mail className='emp-field-icon' />
                                      <span>{selectedEmployee.employeeEmail}</span>
                                    </div>
                                  </div>

                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Phone</label>
                                    <div className='emp-field-value'>
                                      <Phone className='emp-field-icon' />
                                      <span>{selectedEmployee.employeePhoneNumber}</span>
                                    </div>
                                  </div>

                                  {/* Username */}
                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Username</label>
                                    <div className='emp-credential-field'>
                                      <span className='emp-credential-text'>{selectedEmployee.employeUsername}</span>
                                      <button
                                        className='emp-credential-btn'
                                        onClick={() => copyToClipboard(selectedEmployee.employeUsername, "username")}
                                        title='Copy username'
                                      >
                                        <Copy className={`emp-credential-icon ${copiedField === "username" ? "emp-icon-success" : ""}`} />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Password */}
                                  <div className='emp-field'>
                                    <label className='emp-field-label'>Password</label>
                                    <div className='emp-credential-field'>
                                      <span className='emp-credential-text'>{"****************"}</span>
                                      <div className='emp-credential-actions'>
                                        <button
                                          className='emp-credential-btn'
                                          onClick={() => copyToClipboard(selectedEmployee.password, "password")}
                                          title='Copy password'
                                        >
                                          <Copy className={`emp-credential-icon ${copiedField === "password" ? "emp-icon-success" : ""}`} />
                                        </button>
                                        {/*   <button className='emp-credential-btn' onClick={generateNewPassword} title='Reset password'>
                                          <RotateCcw className='emp-credential-icon' />
                                        </button> */}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className='emp-actions'>
                                    {/*  <button className='emp-btn-primary'>Edit Employee</button>
                                    <button className='emp-btn-secondary'>View Activity</button> */}
                                    <button onClick={sendCredentialsToEmail} className='emp-btn-success'>
                                      Send login to email
                                    </button>
                                    {/* <button
                                      style={{
                                        backgroundColor: "#ff4d4d",
                                      }}
                                      className='emp-btn-success'
                                    >
                                      Revoke Access
                                    </button> */}
                                    <button
                                      onClick={() => {
                                        setShowEmployeeActivity(true);
                                      }}
                                      style={{
                                        backgroundColor: "rgba(199, 199, 199, 0.1)",
                                        color: "#000",
                                        border: "1px solid #ccc",
                                      }}
                                      className='emp-btn-success'
                                    >
                                      View activity
                                    </button>
                                    <button
                                      onClick={handleDeleteEmployee}
                                      style={{
                                        backgroundColor: "#ff4d4d",
                                      }}
                                      className='emp-btn-success'
                                    >
                                      Revoke Access and delete
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditEmployeeModalOpen(true);
                                      }}
                                      style={{
                                        backgroundColor: "rgba(199, 199, 199, 0.1)",
                                        color: "#000",
                                        border: "1px solid #ccc",
                                      }}
                                      className='emp-btn-success'
                                    >
                                      Edit {<Pen size={12} />}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {copiedField && <div className='emp-toast'>{copiedField} copied to clipboard!</div>}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className='employeesDivcontainer'>
                  <div className='noEMployeeDiv'>
                    <h3>No employee added yet</h3>
                    <p>
                      Add new employees here and assign them <br /> to a store if you have created one{" "}
                    </p>
                  </div>

                  <button
                    style={{ maxWidth: "280px", marginTop: "50px" }}
                    onClick={() => {
                      if (user.userIsSubscribed && user.freeTrial) {
                        setIsAddEmplyeeOpen(true);
                        return;
                      }
                      if (user.userType.userType.toLowerCase().includes("basic") && user.employees.length >= 1) {
                        uiAlert("Your current plan allows only one employee. Please upgrade your plan to add more employees.", "error");
                        return;
                      }
                      if (user.userType.userType.toLowerCase().includes("super") && user.employees.length >= 2) {
                        uiAlert(" Your current plan allows only two employees. Please upgrade your plan to add more employees.", "error");
                        return;
                      }

                      setIsAddEmplyeeOpen(true);
                    }}
                    className='emp-add-btn'
                  >
                    <Plus className='emp-add-icon' />
                    <span>Add employee</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {isAddEmplyeeOpen && (
          <AddEmployee
            isOpen={isAddEmplyeeOpen}
            onClose={() => setIsAddEmplyeeOpen(false)}
            onAddEmployee={(newEmployee) => {
              setEmployees((prev) => [newEmployee, ...(prev || [])]);
            }}
          />
        )}
      </div>
      {showEmployeeActivity && <ActivityLog setShowEmployeeActivity={setShowEmployeeActivity} employee={selectedEmployee} />}
      {editEmployeeModalOpen && (
        <EditEmployeeOverlay
          employee={selectedEmployee}
          stores={user.store}
          onClose={() => {
            setEditEmployeeModalOpen(false);
          }}
          onSave={(updatedEmp) => {
            handleEditEmployee(updatedEmp);
          }}
        />
      )}
    </>
  );
};
